import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Share2, MessageCircle, ClipboardCopy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * v2.65 — "Share Plans" tool for internal team (admin / sales / callers).
 * Generates a public link to /plans-preview which clients can open WITHOUT
 * registering. Provides one-tap WhatsApp share + Copy Link.
 *
 * Component usage: <SharePlansButton variant="outline|brand|ghost" size="sm" />
 *
 * Visibility gating is the parent's job — this component does NOT check role.
 * Mount it only inside Admin / Team-only screens.
 */
const DEFAULT_MESSAGE =
  "Hi, here are the HouseEntry plans and features.\n" +
  "Explore and choose the option that fits your needs best.\n\n";

export default function SharePlansButton({ variant = "outline", size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [phone, setPhone] = useState("");

  const origin = typeof window !== "undefined" ? window.location.origin : "https://houseentry.in";
  const shareUrl = `${origin}/plans-preview`;
  // OG-aware redirect URL — generates rich preview when WhatsApp unfurls the link
  const ogShareUrl = `${origin}/api/share/plans`;
  const fullMessage = DEFAULT_MESSAGE + ogShareUrl;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ogShareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — long-press the link and copy manually");
    }
  };

  const openWhatsApp = () => {
    const digits = (phone || "").replace(/\D/g, "");
    const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
    const url = `${base}?text=${encodeURIComponent(fullMessage)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={`rounded-sm ${className}`}
        data-testid="share-plans-btn"
      >
        <Share2 size={13} className="mr-1.5" /> Share Plans
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="share-plans-dialog">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-[#EEF4FF] border border-[#C7D9FF] flex items-center justify-center mb-2">
              <Sparkles size={20} className="text-[#0055FF]" />
            </div>
            <DialogTitle className="font-heading tracking-tight text-2xl text-center">
              Share HouseEntry Plans
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-[#52525B] text-center">
              Client ko ek public link bhejein — koi register/login zaroori nahi.
              Khulte hi saare plans aur features dikhenge.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm p-2.5 my-2 flex items-center gap-2">
            <div className="flex-1 text-[11.5px] text-[#52525B] font-mono-data truncate">{ogShareUrl}</div>
            <Button
              size="sm"
              variant="ghost"
              onClick={copyLink}
              className="h-7 px-2 rounded-sm text-[11px]"
              data-testid="share-plans-copy-link"
            >
              {copied ? (<><Check size={12} className="mr-1 text-[#16A34A]" /> Copied</>) : (<><ClipboardCopy size={12} className="mr-1" /> Copy</>)}
            </Button>
          </div>

          <div className="space-y-2 mt-2">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">
              Send to a specific number (optional)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+ ]/g, ""))}
              placeholder="+91 98xxxxxxxx (leave blank to pick a contact)"
              className="w-full h-10 px-3 rounded-sm border border-[#E4E4E7] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30"
              data-testid="share-plans-phone-input"
            />
          </div>

          <Button
            onClick={openWhatsApp}
            className="w-full mt-3 bg-[#25D366] hover:bg-[#1FAB54] text-white rounded-sm h-11 text-[13.5px] font-bold"
            data-testid="share-plans-wa-btn"
          >
            <MessageCircle size={14} className="mr-1.5" /> Open WhatsApp & Send
          </Button>

          <div className="mt-3 text-[11px] text-[#A1A1AA] text-center leading-relaxed">
            Auto-message: <span className="font-mono">"Hi, here are the HouseEntry plans and features. Explore and choose the option that fits your needs best."</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
